import React, { useEffect, useState, useCallback } from 'react';
import friendlyError from '../../utils/friendlyError';
import styled from 'styled-components';
import { FaSyncAlt, FaMagic, FaAddressBook, FaChartBar, FaExclamationTriangle, FaCopy, FaPlus, FaPen, FaTrash, FaSave, FaTimes } from 'react-icons/fa';
import {
  TechShell, TechPageHeader, TechSection, TechTabs, TechInput, TechSelect, TechTextarea,
  TechButton, TechButtonGhost, StatStrip, StatTile, StatLabel, StatValue, StatSub, StatusPill,
  TechChip, Empty, Loading, ErrorBox, borderSoft, mono, accent,
} from '../../styles/techComponents';
import { getCoverage, listContacts, generarGacetilla, createContact, updateContact, deleteContact } from '../../services/prensaService';

const Bar = styled.div` height:9px;border-radius:5px;background:rgba(255,255,255,0.06);overflow:hidden;min-width:60px;
  > span{display:block;height:100%;background:${({ $c }) => $c};width:${({ $w }) => $w}%} `;
const Rowc = styled.div` display:grid;grid-template-columns:1.4fr 60px 1fr 60px 1fr;gap:0.5rem;align-items:center;
  padding:0.5rem 0;border-bottom:1px solid ${borderSoft};font-size:0.85rem;
  .n{font-family:${mono};font-variant-numeric:tabular-nums;text-align:right} `;
const CList = styled.div` display:grid;grid-template-columns:repeat(auto-fill,minmax(240px,1fr));gap:0.6rem; `;
const CCard = styled.div` border:1px solid ${borderSoft};border-radius:8px;padding:0.7rem 0.8rem;background:rgba(11,19,32,0.4);
  h5{margin:0 0 0.2rem;font-size:0.9rem} .m{font-family:${mono};font-size:0.72rem;color:var(--text-secondary)} `;
const FormGrid = styled.div` display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:0.6rem;margin-bottom:0.7rem; `;
const CardActions = styled.div` display:flex;gap:0.4rem;margin-top:0.6rem;
  button{padding:0.25rem 0.5rem;font-size:0.72rem} `;
const TENDENCIAS = ['aliado', 'neutral', 'hostil', 'desconocido'];
const emptyContact = { nombre: '', medio: '', rol: '', email: '', telefono: '', tendencia: 'desconocido', notas: '' };

export default function Prensa() {
  const [tab, setTab] = useState('cobertura');
  const [cov, setCov] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gForm, setGForm] = useState({ tema: '', outlet_id: '', angulo: '' });
  const [gOut, setGOut] = useState(null);
  const [gBusy, setGBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [cForm, setCForm] = useState(emptyContact);
  const [cBusy, setCBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [eForm, setEForm] = useState(emptyContact);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { const [c, cs] = await Promise.all([getCoverage(), listContacts()]); setCov(c); setContacts(cs); }
    catch (e) { setError(friendlyError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(null), 2500); };

  async function onGenerar() {
    if (!gForm.tema.trim()) return flash('Poné un tema');
    setGBusy(true); setGOut(null);
    try {
      const r = await generarGacetilla({ tema: gForm.tema, outlet_id: gForm.outlet_id || undefined, angulo: gForm.angulo || undefined });
      setGOut(r); flash('Gacetilla generada → Bandeja (pendiente)');
    } catch (e) { flash(e?.response?.data?.error || 'Error'); }
    finally { setGBusy(false); }
  }

  async function onCrearContacto() {
    if (!cForm.nombre.trim()) return flash('Poné un nombre');
    setCBusy(true);
    try {
      await createContact(cForm);
      setCForm(emptyContact);
      await load();
      flash('Contacto creado');
    } catch (e) { flash(e?.response?.data?.error || 'Error al crear'); }
    finally { setCBusy(false); }
  }

  function startEdit(c) {
    setEditId(c.id);
    setEForm({
      nombre: c.nombre || '', medio: c.outlet_name || c.medio || '', rol: c.rol || '',
      email: c.email || '', telefono: c.telefono || '', tendencia: c.tendencia || 'desconocido', notas: c.notas || '',
    });
  }
  function cancelEdit() { setEditId(null); setEForm(emptyContact); }

  async function onGuardarContacto(id) {
    if (!eForm.nombre.trim()) return flash('Poné un nombre');
    try {
      await updateContact({ id, ...eForm });
      cancelEdit();
      await load();
      flash('Contacto actualizado');
    } catch (e) { flash(e?.response?.data?.error || 'Error al guardar'); }
  }

  async function onBorrarContacto(c) {
    if (!window.confirm(`¿Borrar el contacto "${c.nombre}"?`)) return;
    try {
      await deleteContact(c.id);
      if (editId === c.id) cancelEdit();
      await load();
      flash('Contacto borrado');
    } catch (e) { flash(e?.response?.data?.error || 'Error al borrar'); }
  }

  if (loading) return <TechShell><Loading>Cargando prensa…</Loading></TechShell>;
  if (error) return <TechShell><ErrorBox><FaExclamationTriangle /> {error}</ErrorBox></TechShell>;

  const totals = cov?.totals || { chouhy: 0, passaglia: 0 };
  const outlets = cov?.outlets || [];

  return (
    <TechShell>
      <TechPageHeader
        title="Motor de Prensa"
        dossier="MEDIOS · SN"
        subtitle="Quién te cubre vs Passaglia, tu agenda de contactos y gacetillas dirigidas para cerrar la brecha mediática."
        actions={<TechButtonGhost onClick={load}><FaSyncAlt /> Actualizar</TechButtonGhost>}
      />

      <StatStrip>
        <StatTile><StatLabel>Notas Chouhy (60d)</StatLabel><StatValue style={{ color: '#38d39f' }}>{totals.chouhy}</StatValue><StatSub>en medios locales</StatSub></StatTile>
        <StatTile><StatLabel>Notas Passaglia (60d)</StatLabel><StatValue style={{ color: '#f87171' }}>{totals.passaglia}</StatValue><StatSub>en medios locales</StatSub></StatTile>
        <StatTile><StatLabel>Medios oportunidad</StatLabel><StatValue>{(cov?.oportunidades || []).length}</StatValue><StatSub>cubren a Passaglia, a vos no</StatSub></StatTile>
        <StatTile><StatLabel>Contactos</StatLabel><StatValue>{contacts.length}</StatValue><StatSub>en la agenda</StatSub></StatTile>
      </StatStrip>

      {msg && <StatusPill style={{ marginBottom: '0.5rem' }}>{msg}</StatusPill>}

      <TechTabs
        tabs={[{ id: 'cobertura', label: 'Cobertura' }, { id: 'contactos', label: `Contactos (${contacts.length})` }, { id: 'gacetilla', label: 'Gacetilla IA' }]}
        active={tab} onChange={setTab}
      />

      {tab === 'cobertura' && (
        <TechSection title="Cobertura por medio · vos vs Passaglia (60 días)" icon={<FaChartBar />}>
          {(cov?.oportunidades || []).length > 0 && (
            <div style={{ marginBottom: '0.8rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              🎯 <b style={{ color: accent }}>Medios a trabajar</b> (cubren a Passaglia y a vos no): {cov.oportunidades.map((o) => o.name).join(' · ')}
            </div>
          )}
          {outlets.filter((o) => o.chouhy + o.passaglia > 0).map((o) => {
            const max = Math.max(o.chouhy, o.passaglia, 1);
            return (
              <Rowc key={o.id}>
                <span>{o.name}</span>
                <span className="n" style={{ color: '#38d39f' }}>{o.chouhy}</span>
                <Bar $c="#38d39f" $w={Math.round((o.chouhy / max) * 100)}><span /></Bar>
                <span className="n" style={{ color: '#f87171' }}>{o.passaglia}</span>
                <Bar $c="#f87171" $w={Math.round((o.passaglia / max) * 100)}><span /></Bar>
              </Rowc>
            );
          })}
          {outlets.every((o) => o.chouhy + o.passaglia === 0) && <Empty>Sin menciones detectadas en la ventana. El monitoreo de medios corre a diario.</Empty>}
        </TechSection>
      )}

      {tab === 'contactos' && (
        <TechSection title="Agenda de prensa" icon={<FaAddressBook />}>
          <div style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: `1px solid ${borderSoft}` }}>
            <StatLabel>Nuevo contacto</StatLabel>
            <FormGrid style={{ marginTop: '0.4rem' }}>
              <TechInput placeholder="Nombre *" value={cForm.nombre} onChange={(e) => setCForm({ ...cForm, nombre: e.target.value })} />
              <TechInput placeholder="Medio" value={cForm.medio} onChange={(e) => setCForm({ ...cForm, medio: e.target.value })} />
              <TechInput placeholder="Rol (ej: periodista)" value={cForm.rol} onChange={(e) => setCForm({ ...cForm, rol: e.target.value })} />
              <TechInput placeholder="Email" value={cForm.email} onChange={(e) => setCForm({ ...cForm, email: e.target.value })} />
              <TechInput placeholder="Teléfono" value={cForm.telefono} onChange={(e) => setCForm({ ...cForm, telefono: e.target.value })} />
              <TechSelect value={cForm.tendencia} onChange={(e) => setCForm({ ...cForm, tendencia: e.target.value })}>
                {TENDENCIAS.map((t) => <option key={t} value={t}>{t}</option>)}
              </TechSelect>
            </FormGrid>
            <TechTextarea placeholder="Notas (opcional)" value={cForm.notas} onChange={(e) => setCForm({ ...cForm, notas: e.target.value })} style={{ minHeight: 60 }} />
            <div style={{ marginTop: '0.6rem' }}>
              <TechButton onClick={onCrearContacto} disabled={cBusy}><FaPlus /> {cBusy ? 'Creando…' : 'Agregar contacto'}</TechButton>
            </div>
          </div>
          <CList>
            {contacts.map((c) => (
              <CCard key={c.id}>
                {editId === c.id ? (
                  <>
                    <FormGrid style={{ gridTemplateColumns: '1fr', gap: '0.4rem', marginBottom: '0.4rem' }}>
                      <TechInput placeholder="Nombre *" value={eForm.nombre} onChange={(e) => setEForm({ ...eForm, nombre: e.target.value })} />
                      <TechInput placeholder="Medio" value={eForm.medio} onChange={(e) => setEForm({ ...eForm, medio: e.target.value })} />
                      <TechInput placeholder="Rol" value={eForm.rol} onChange={(e) => setEForm({ ...eForm, rol: e.target.value })} />
                      <TechInput placeholder="Email" value={eForm.email} onChange={(e) => setEForm({ ...eForm, email: e.target.value })} />
                      <TechInput placeholder="Teléfono" value={eForm.telefono} onChange={(e) => setEForm({ ...eForm, telefono: e.target.value })} />
                      <TechSelect value={eForm.tendencia} onChange={(e) => setEForm({ ...eForm, tendencia: e.target.value })}>
                        {TENDENCIAS.map((t) => <option key={t} value={t}>{t}</option>)}
                      </TechSelect>
                      <TechTextarea placeholder="Notas" value={eForm.notas} onChange={(e) => setEForm({ ...eForm, notas: e.target.value })} style={{ minHeight: 50 }} />
                    </FormGrid>
                    <CardActions>
                      <TechButton onClick={() => onGuardarContacto(c.id)}><FaSave /> Guardar</TechButton>
                      <TechButtonGhost onClick={cancelEdit}><FaTimes /> Cancelar</TechButtonGhost>
                    </CardActions>
                  </>
                ) : (
                  <>
                    <h5>{c.nombre}</h5>
                    <div className="m">{c.outlet_name || c.medio || '—'}</div>
                    <div className="m">{c.rol}{c.email ? ` · ${c.email}` : ''}</div>
                    {c.telefono && <div className="m">{c.telefono}</div>}
                    <div style={{ marginTop: 6 }}><TechChip>{c.tendencia || 'desconocido'}</TechChip></div>
                    <CardActions>
                      <TechButtonGhost onClick={() => startEdit(c)}><FaPen /> Editar</TechButtonGhost>
                      <TechButtonGhost onClick={() => onBorrarContacto(c)}><FaTrash /> Borrar</TechButtonGhost>
                    </CardActions>
                  </>
                )}
              </CCard>
            ))}
          </CList>
          {contacts.length === 0 && <Empty>Sin contactos. Cargá el primero con el formulario de arriba.</Empty>}
        </TechSection>
      )}

      {tab === 'gacetilla' && (
        <TechSection title="Generar gacetilla dirigida" icon={<FaMagic />}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0.6rem', marginBottom: '0.6rem' }}>
            <div><StatLabel>Tema</StatLabel><TechInput placeholder="Ej: pedido de informes por el Puerto" value={gForm.tema} onChange={(e) => setGForm({ ...gForm, tema: e.target.value })} /></div>
            <div><StatLabel>Medio (opcional)</StatLabel>
              <TechSelect value={gForm.outlet_id} onChange={(e) => setGForm({ ...gForm, outlet_id: e.target.value })}>
                <option value="">— General —</option>
                {outlets.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </TechSelect>
            </div>
          </div>
          <StatLabel>Ángulo (opcional)</StatLabel>
          <TechInput placeholder="Ej: transparencia y control del gasto" value={gForm.angulo} onChange={(e) => setGForm({ ...gForm, angulo: e.target.value })} />
          <div style={{ marginTop: '0.7rem' }}>
            <TechButton onClick={onGenerar} disabled={gBusy}><FaMagic /> {gBusy ? 'Generando…' : 'Generar gacetilla'}</TechButton>
          </div>
          {gOut && (
            <div style={{ marginTop: '1rem' }}>
              <StatLabel>Gacetilla {gOut.outlet ? `· ${gOut.outlet}` : ''}</StatLabel>
              <TechTextarea readOnly value={gOut.cuerpo} style={{ minHeight: 260, fontFamily: mono, fontSize: '0.82rem' }} />
              <div style={{ marginTop: 6 }}>
                <TechButtonGhost onClick={() => { navigator.clipboard?.writeText(gOut.cuerpo); flash('Copiado'); }}><FaCopy /> Copiar</TechButtonGhost>
                <span style={{ marginLeft: 10, fontSize: '0.78rem', color: 'var(--text-secondary)' }}>Guardada en la Bandeja de Mensajería (pendiente de aprobación).</span>
              </div>
            </div>
          )}
        </TechSection>
      )}
    </TechShell>
  );
}
